import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer, Legend
} from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useApi } from '../hooks/useApi';

function fmtMoney(val) {
  if (val == null) return '—';
  const n = Number(val);
  if (isNaN(n)) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  return `$${Math.round(n).toLocaleString()}`;
}

const TREND_ICON = {
  Increasing:   { icon: TrendingUp,   color: 'text-red-500'  },
  Decreasing:   { icon: TrendingDown, color: 'text-green-500' },
  Stable:       { icon: Minus,        color: 'text-gray-400'  },
  Insufficient: { icon: Minus,        color: 'text-gray-300'  },
};

function TrendChartSkeleton() {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-4 h-4 rounded-full bg-gray-200 animate-pulse" />
        <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="h-[140px] bg-gray-100 rounded animate-pulse" />
      <div className="h-3 w-56 bg-gray-200 rounded animate-pulse mt-2" />
    </div>
  );
}

// Shown when the calc has completed but no simulation snapshot exists yet (e.g. FAIR scenario
// enrichment hasn't produced financials for this assessment). Keeps the card in place — never blank.
function TrendChartEmpty() {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Minus className="w-4 h-4 text-gray-300" />
        <span className="text-sm font-semibold text-gray-800">Portfolio Loss Trend</span>
        <span className="text-xs text-gray-400">(last 90 days)</span>
      </div>
      <div className="h-[140px] flex items-center justify-center text-center px-4">
        <p className="text-xs text-gray-400">
          Trend will appear once the first simulation is calculated for this assessment.
        </p>
      </div>
    </div>
  );
}

export default function RansomwareProgressTrendChart({ assessmentId, analyticsVersion = 0 }) {
  const [trend, setTrend] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const apiCall = useApi();

  useEffect(() => {
    // Wait for the analytics calc to finish before fetching. That call (GetAssessmentRisks) is what
    // writes the simulation snapshot this chart reads — fetching earlier would race it and 204.
    if (!assessmentId || !analyticsVersion) return;
    let cancelled = false;
    setLoaded(false);
    apiCall('GET', `/ransomwareRisks/assessment-risks/${assessmentId}/trend?days=90`)
      .then(d => { if (!cancelled) { setTrend(d ?? null); setLoaded(true); } })
      .catch(() => { if (!cancelled) { setTrend(null); setLoaded(true); } });
    return () => { cancelled = true; };
  }, [assessmentId, analyticsVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Until the calc has completed and the fetch resolves, show a skeleton rather than a blank gap.
  if (!analyticsVersion || !loaded) return <TrendChartSkeleton />;
  if (!trend || !trend.points?.length) return <TrendChartEmpty />;

  const TrendIcon = TREND_ICON[trend.trendDirection]?.icon ?? Minus;
  const trendColor = TREND_ICON[trend.trendDirection]?.color ?? 'text-gray-400';

  // First point where signal amplification enabled (for cutover line)
  const cutoverPoint = trend.points.find(p => p.isSignalAmplified);

  const chartData = trend.points.map(p => ({
    date:       new Date(p.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    p90:        p.p90 != null ? Number((p.p90 / 1_000_000).toFixed(2)) : null,
    p50:        p.p50 != null ? Number((p.p50 / 1_000_000).toFixed(2)) : null,
    confidence: p.confidenceScore,
    amplified:  p.isSignalAmplified,
  }));

  // Issue 1B: auto-scale Y-axis to data range with 15% padding
  const values = chartData.flatMap(p => [p.p90, p.p50]).filter(v => v != null);
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const padding = (dataMax - dataMin) * 0.15 || dataMax * 0.05;
  const yDomain = [Math.max(0, dataMin - padding), dataMax + padding];

  const cutoverLabel = cutoverPoint
    ? new Date(cutoverPoint.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendIcon className={`w-4 h-4 ${trendColor}`} />
          <span className="text-sm font-semibold text-gray-800">Portfolio Loss Trend</span>
          <span className="text-xs text-gray-400">(last 90 days)</span>
        </div>
        <div className="text-xs text-gray-500" title="Previous simulation → current simulation (same baseline as Portfolio Intelligence Brief)">
          {trend.points.length >= 2
            ? <>{fmtMoney(trend.previousP90 ?? trend.earliestP90)} → <span className="font-semibold">{fmtMoney(trend.latestP90)}</span></>
            : <span className="font-semibold">{fmtMoney(trend.latestP90)}</span>}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={140}>
        <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
          <YAxis
            tick={{ fontSize: 10 }}
            tickFormatter={v => `$${v.toFixed(1)}M`}
            width={55}
            domain={yDomain}
          />
          <Tooltip
            formatter={(v, name) => [`$${v}M`, name === 'p90' ? 'P90' : 'P50']}
            labelStyle={{ fontSize: 11 }}
            contentStyle={{ fontSize: 11 }}
          />
          <Legend
            iconType="line"
            iconSize={10}
            formatter={v => v === 'p90' ? 'P90 (stress scenario)' : 'P50 (median)'}
            wrapperStyle={{ fontSize: 11 }}
          />
          {cutoverLabel && (
            <ReferenceLine
              x={cutoverLabel}
              stroke="#8b5cf6"
              strokeDasharray="4 2"
              label={{ value: 'Signals live', position: 'insideTopRight', fontSize: 9, fill: '#8b5cf6' }}
            />
          )}
          <Line
            type="monotone"
            dataKey="p90"
            stroke="#f97316"
            strokeWidth={2}
            dot={({ cx, cy, payload }) => (
              <circle
                key={`dot-p90-${cx}`}
                cx={cx} cy={cy} r={3}
                fill="#f97316"
                // Floor the confidence-driven opacity so a low-confidence point (and especially a
                // lone first snapshot) is always visible rather than fading the chart to blank.
                opacity={Math.max(0.4, (payload.confidence ?? 100) / 100)}
              />
            )}
            activeDot={{ r: 4 }}
          />
          <Line
            type="monotone"
            dataKey="p50"
            stroke="#9ca3af"
            strokeWidth={1.5}
            dot={false}
            activeDot={{ r: 3 }}
          />
        </LineChart>
      </ResponsiveContainer>

      <p className="text-xs text-gray-400 mt-1">
        Dot opacity reflects simulation confidence. Dashed line marks signal amplification activation.
      </p>
    </div>
  );
}
